// NPM packages
import React, { useEffect, useState } from 'react';
import { styled } from '@mui/system';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// All other imports
import PageContent from 'components/core/PageContent';
import CircularProgress from 'components/core/CircularProgress';
import NotFoundContent from 'components/core/NotFoundContent';

const Root = styled('div')({
  fontFamily: "'Roboto', sans-serif",
  fontSize: '1.125rem',
  lineHeight: 1.6,
  maxWidth: '960px',
  margin: '0 auto',
  color: '#000',
  '& h1': {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: '1.5rem',
  },
  '& h2': {
    fontSize: '1.75rem',
    fontWeight: 600,
    marginTop: '2rem',
    marginBottom: '0.75rem',
  },
  '& p': {
    marginBottom: '1.25rem',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'auto',
    marginBottom: '1.5rem',
    fontSize: '1rem',
  },
  '& thead': {
    background: '#093e80',
  },
  '& th': {
    color: '#fff',
    fontWeight: 700,
    padding: '0.75rem 1rem',
    textAlign: 'left',
    borderBottom: '1px solid #ccc',
    whiteSpace: 'nowrap',
  },
  '& td': {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  // Allow only the last column (Description) to wrap
  '& td:last-child, & th:last-child': {
    whiteSpace: 'normal',
    width: '100%',
  },
  '& tbody tr:hover': {
    background: '#f4f6fa',
  },
  '& a': {
    color: '#1b75bb',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
});

export default function Events(): React.ReactElement {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    fetch('/content/events.md')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(setContent)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <PageContent>
        <NotFoundContent />
      </PageContent>
    );
  }

  if (content === null) {
    return (
      <PageContent>
        <CircularProgress text="Loading events..." />
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Root>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </Root>
    </PageContent>
  );
}